import React, { useEffect, useMemo, useState } from 'react';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import { schoolClassSortOrder } from '../services/TimetableService';
import { studentBehaviorService, categoriesFor } from '../services/StudentBehaviorService';
import { schoolMatches } from '../utils/schoolScope';
import './StudentBehaviorView.css';
import './StudentDetailPages.css';

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString() : '');

function StudentBehaviorView({ user, mode = 'admin', studentIds = null }) {
  const readOnly = mode === 'parent';
  const isSuper = mode === 'superadmin';
  const [schools, setSchools] = useState([]);
  const [schoolFilter, setSchoolFilter] = useState('');
  const [behaviors, setBehaviors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [showReport, setShowReport] = useState(false);
  const [classes, setClasses] = useState([]);
  const [students, setStudents] = useState([]);
  const [form, setForm] = useState({ classId: '', studentKey: '', behaviorType: 'Negative', category: '', note: '' });
  const [saving, setSaving] = useState(false);
  const [selected, setSelected] = useState(null);
  const [reportStudent, setReportStudent] = useState('');
  const [reportFrom, setReportFrom] = useState('');
  const [reportTo, setReportTo] = useState('');

  const activeSchoolId = isSuper ? (schoolFilter || null) : (user?.schoolId || null);
  const idsKey = (studentIds || []).join(',');

  useEffect(() => {
    if (!isSuper) return;
    getDocs(collection(db, 'schools')).then(s => setSchools(s.docs.map(d => ({ id: d.id, ...d.data() })))).catch(() => {});
  }, [isSuper]);

  useEffect(() => {
    setLoading(true);
    const unsub = studentBehaviorService.subscribeToBehaviors(
      readOnly ? { studentIds: studentIds || [] } : { schoolId: activeSchoolId },
      (list) => { setBehaviors(list); setLoading(false); }
    );
    return () => unsub();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSchoolId, readOnly, idsKey]);

  useEffect(() => {
    if (readOnly || !showForm) return;
    (async () => {
      const base = collection(db, 'classes');
      const snap = await getDocs(base);
      // Case-insensitive school match — iOS stores uppercase UUIDs
      setClasses(snap.docs.map(d => ({ id: d.id, ...d.data() }))
        .filter(c => !activeSchoolId || schoolMatches(c.schoolId, activeSchoolId))
        .sort(schoolClassSortOrder));
    })().catch(console.error);
  }, [readOnly, showForm, activeSchoolId]);

  useEffect(() => {
    if (!form.classId) { setStudents([]); return; }
    getDocs(query(collection(db, 'classes', form.classId, 'students'), where('isActive', '==', true)))
      .then(s => setStudents(s.docs.map(d => ({ id: d.id, ...d.data() })).sort((a, b) => (a.fullName || '').localeCompare(b.fullName || ''))))
      .catch(console.error);
  }, [form.classId]);

  const stats = useMemo(() => {
    const pos = behaviors.filter(b => b.behaviorType === 'Positive').length;
    const neg = behaviors.length - pos;
    const week = Date.now() - 7 * 86400000;
    return {
      total: behaviors.length, pos, neg,
      students: new Set(behaviors.map(b => b.studentId)).size,
      week: behaviors.filter(b => new Date(b.date).getTime() >= week).length,
      ratio: behaviors.length ? Math.round((pos / behaviors.length) * 100) : 0
    };
  }, [behaviors]);

  const filtered = useMemo(() => behaviors.filter(b =>
    (typeFilter === 'all' || b.behaviorType === typeFilter) &&
    (!search || `${b.studentName} ${b.category} ${b.note}`.toLowerCase().includes(search.toLowerCase()))
  ), [behaviors, typeFilter, search]);

  const reportRows = useMemo(() => behaviors.filter(b => {
    const t = new Date(b.date).getTime();
    return (!reportStudent || b.studentId === reportStudent) &&
      (!reportFrom || t >= new Date(reportFrom).getTime()) &&
      (!reportTo || t <= new Date(reportTo).getTime() + 86399999);
  }), [behaviors, reportStudent, reportFrom, reportTo]);

  const reportStudents = useMemo(() => {
    const m = new Map();
    behaviors.forEach(b => m.set(b.studentId, b.studentName));
    return [...m.entries()].sort((a, b) => a[1].localeCompare(b[1]));
  }, [behaviors]);

  const reportSummary = useMemo(() => {
    const byCat = {};
    reportRows.forEach(b => { byCat[b.category] = (byCat[b.category] || 0) + 1; });
    const pos = reportRows.filter(b => b.behaviorType === 'Positive').length;
    const pct = reportRows.length ? Math.round((pos / reportRows.length) * 100) : 0;
    const rating = !reportRows.length ? 'N/A' : pct >= 80 ? 'Excellent' : pct >= 60 ? 'Good' : pct >= 40 ? 'Fair' : 'Needs Improvement';
    return { byCat, pos, neg: reportRows.length - pos, pct, rating };
  }, [reportRows]);

  const handleSave = async (e) => {
    e.preventDefault();
    const student = students.find(s => s.id === form.studentKey);
    if (!student || !form.category) return;
    setSaving(true);
    try {
      await studentBehaviorService.addBehavior({
        studentId: student.studentId || student.id,
        studentDocumentId: student.id,
        studentName: student.fullName || `${student.firstName || ''} ${student.lastName || ''}`.trim(),
        studentPhotoURL: student.photoURL || '',
        classId: form.classId,
        schoolId: student.schoolId || activeSchoolId || '',
        category: form.category,
        behaviorType: form.behaviorType,
        note: form.note.trim(),
        reportedById: user?.uid || '',
        reportedByName: user?.displayName || 'Staff',
        reportedByRole: mode === 'superadmin' ? 'Super Admin' : mode === 'admin' ? 'Admin' : 'Teacher'
      });
      setShowForm(false);
      setForm({ classId: '', studentKey: '', behaviorType: 'Negative', category: '', note: '' });
    } catch (err) {
      console.error(err);
      alert('Failed to save behavior entry');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (b) => {
    if (!window.confirm(`Delete this entry for ${b.studentName}?`)) return;
    try { await studentBehaviorService.deleteBehavior(b.id); } catch (err) { alert('Failed to delete entry'); }
  };

  const exportCsv = () => {
    const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const rows = [['Date', 'Student', 'Type', 'Category', 'Note', 'Reported By'],
      ...reportRows.map(b => [fmtDate(b.date), b.studentName, b.behaviorType, b.category, b.note, `${b.reportedByName} (${b.reportedByRole})`])];
    const blob = new Blob([rows.map(r => r.map(esc).join(',')).join('\n')], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = 'behavior-report.csv';
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const cards = [
    ['Total', stats.total, 'blue'], ['Positive', stats.pos, 'green'], ['Negative', stats.neg, 'red'],
    ['Students', stats.students, 'purple'], ['This Week', stats.week, 'orange'], ['Positive %', `${stats.ratio}%`, 'teal']
  ];

  const schoolNameFor = (sid) => schools.find(s => schoolMatches(s.id, sid))?.name || '';

  if (loading) return <div className="loading-container"><div className="spinner"></div><p>Loading behavior...</p></div>;

  if (selected) {
    const typeKey = (selected.behaviorType || 'Negative').toLowerCase();
    console.log('🔥 StudentBehavior: Opened entry detail', selected.id);
    return (
      <div className="detail-page">
        <button className="detail-back-btn" onClick={() => setSelected(null)}>◄ Back</button>
        <div className="detail-hero">
          <div className="detail-hero-icon">{typeKey === 'positive' ? '🌟' : '⚠️'}</div>
          <div>
            <h2 className="detail-page-title">
              {selected.studentName || 'Student'}
              <span className={`detail-badge ${typeKey}`}>{selected.behaviorType || 'Negative'}</span>
            </h2>
            <p className="detail-page-subtitle">{selected.category || 'Behavior entry'} · {fmtDate(selected.date)}</p>
          </div>
        </div>

        <div className="detail-card">
          <h4>Details</h4>
          <div className="detail-rows">
            <div className="detail-row"><span className="detail-label">Student</span><span className="detail-value">{selected.studentName || 'N/A'}</span></div>
            <div className="detail-row"><span className="detail-label">Type</span><span className="detail-value">{selected.behaviorType || 'N/A'}</span></div>
            <div className="detail-row"><span className="detail-label">Category</span><span className="detail-value">{selected.category || 'N/A'}</span></div>
            <div className="detail-row"><span className="detail-label">Date</span><span className="detail-value">{fmtDate(selected.date)}</span></div>
            <div className="detail-row"><span className="detail-label">Reported By</span><span className="detail-value">{selected.reportedByName || 'N/A'}{selected.reportedByRole ? ` (${selected.reportedByRole})` : ''}</span></div>
            {schoolNameFor(selected.schoolId) && (
              <div className="detail-row"><span className="detail-label">School</span><span className="detail-value">{schoolNameFor(selected.schoolId)}</span></div>
            )}
          </div>
        </div>

        {selected.note && (
          <div className="detail-card">
            <h4>Note</h4>
            <p className="detail-note">{selected.note}</p>
          </div>
        )}

        {!readOnly && (
          <div className="detail-actions">
            <button className="btn-delete" onClick={() => { handleDelete(selected); setSelected(null); }}>Delete Entry</button>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="behavior-view">
      <div className="view-header">
        <h2>Student Behavior</h2>
        <div className="behavior-actions">
          {isSuper && (
            <select value={schoolFilter} onChange={(e) => setSchoolFilter(e.target.value)}>
              <option value="">All Schools</option>
              {schools.map(s => <option key={s.id} value={s.id}>{s.name || s.id}</option>)}
            </select>
          )}
          {!readOnly && <button className="behavior-btn secondary" onClick={() => setShowReport(true)}>Report</button>}
          {!readOnly && <button className="behavior-btn" onClick={() => setShowForm(true)}>+ Add Entry</button>}
        </div>
      </div>

      <div className="behavior-stats">
        {cards.map(([label, value, color]) => (
          <div key={label} className={`behavior-stat ${color}`}>
            <div className="behavior-stat-value">{value}</div>
            <div className="behavior-stat-label">{label}</div>
          </div>
        ))}
      </div>

      <div className="behavior-filters">
        <input type="text" placeholder="Search student, category, note..." value={search} onChange={(e) => setSearch(e.target.value)} />
        <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
          <option value="all">All</option>
          <option value="Positive">Positive</option>
          <option value="Negative">Negative</option>
        </select>
      </div>

      {filtered.length === 0 ? (
        <div className="empty-state"><div className="empty-icon">📋</div><h3>No behavior entries</h3></div>
      ) : (
        <div className="behavior-list">
          {filtered.map(b => (
            <div key={b.id} className={`behavior-card ${b.behaviorType === 'Positive' ? 'positive' : 'negative'}`} style={{ cursor: 'pointer' }} onClick={() => setSelected(b)}>
              <div className="behavior-card-main">
                <h4>{b.studentName}</h4>
                <span className={`behavior-badge ${b.behaviorType === 'Positive' ? 'positive' : 'negative'}`}>{b.behaviorType}</span>
                <span className="behavior-category">{b.category}</span>
                {b.note && <p className="behavior-note">{b.note}</p>}
                <small>{fmtDate(b.date)} · {b.reportedByName} ({b.reportedByRole}){isSuper && schoolNameFor(b.schoolId) ? ` · ${schoolNameFor(b.schoolId)}` : ''}</small>
              </div>
              {!readOnly && <button className="behavior-delete" onClick={(e) => { e.stopPropagation(); handleDelete(b); }}>Delete</button>}
            </div>
          ))}
        </div>
      )}

      {showForm && (
        <div className="modal-overlay" onClick={() => setShowForm(false)}>
          <form className="modal behavior-modal" onClick={(e) => e.stopPropagation()} onSubmit={handleSave}>
            <h3>Add Behavior Entry</h3>
            <label>Class
              <select required value={form.classId} onChange={(e) => setForm({ ...form, classId: e.target.value, studentKey: '' })}>
                <option value="">Select class</option>
                {classes.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </label>
            <label>Student
              <select required value={form.studentKey} onChange={(e) => setForm({ ...form, studentKey: e.target.value })}>
                <option value="">Select student</option>
                {students.map(s => <option key={s.id} value={s.id}>{s.fullName}</option>)}
              </select>
            </label>
            <label>Type
              <select value={form.behaviorType} onChange={(e) => setForm({ ...form, behaviorType: e.target.value, category: '' })}>
                <option value="Positive">Positive</option>
                <option value="Negative">Negative</option>
              </select>
            </label>
            <label>Category
              <select required value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
                <option value="">Select category</option>
                {categoriesFor(form.behaviorType).map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </label>
            <label>Note
              <textarea rows={3} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} />
            </label>
            <div className="modal-actions">
              <button type="button" className="behavior-btn secondary" onClick={() => setShowForm(false)}>Cancel</button>
              <button type="submit" className="behavior-btn" disabled={saving}>{saving ? 'Saving...' : 'Save'}</button>
            </div>
          </form>
        </div>
      )}

      {showReport && (
        <div className="modal-overlay" onClick={() => setShowReport(false)}>
          <div className="modal behavior-modal behavior-report" onClick={(e) => e.stopPropagation()}>
            <h3>Behavior Report</h3>
            <div className="behavior-filters">
              <select value={reportStudent} onChange={(e) => setReportStudent(e.target.value)}>
                <option value="">All students</option>
                {reportStudents.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
              </select>
              <input type="date" value={reportFrom} onChange={(e) => setReportFrom(e.target.value)} />
              <input type="date" value={reportTo} onChange={(e) => setReportTo(e.target.value)} />
            </div>
            <p><strong>Entries:</strong> {reportRows.length} · <strong>Positive:</strong> {reportSummary.pos} · <strong>Negative:</strong> {reportSummary.neg} · <strong>Positive ratio:</strong> {reportSummary.pct}% · <strong>Rating:</strong> {reportSummary.rating}</p>
            <ul>
              {Object.entries(reportSummary.byCat).sort((a, b) => b[1] - a[1]).map(([c, n]) => <li key={c}>{c}: {n}</li>)}
            </ul>
            <div className="modal-actions">
              <button className="behavior-btn secondary" onClick={() => setShowReport(false)}>Close</button>
              <button className="behavior-btn secondary" onClick={() => window.print()}>Print / PDF</button>
              <button className="behavior-btn" onClick={exportCsv} disabled={!reportRows.length}>Export CSV</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default StudentBehaviorView;
